import { THRESHOLDS, PRECISION } from '../constants'
import { formatRelativeTime, formatTimestamp } from '../utils/formatters'
import type { VehicleState } from '../types'
import './VehicleStateCard.css'

interface VehicleStateCardProps {
  vehicleState: VehicleState | null
}

type GaugeKind = 'fuel' | 'adblue' | 'dpf'

// End-of-drive snapshot. Readings stay at full opacity when they are old:
// staleness is the normal case here, not a fault.
export function VehicleStateCard({ vehicleState }: VehicleStateCardProps) {
  const fuelLiters = vehicleState?.fuel_level_liters
  const fuelPercent = vehicleState?.fuel_level_percent
  const adblueLiters = vehicleState?.adblue_level_liters
  const adbluePercent = vehicleState?.adblue_level_percent
  const dpfPercent = vehicleState?.dpf_soot_level_percent
  const outdoorTemp = vehicleState?.outdoor_temperature_c
  const rangeKm = vehicleState?.estimated_range_km

  const hasReading = vehicleState != null && (
    fuelLiters != null || fuelPercent != null ||
    adblueLiters != null || adbluePercent != null ||
    dpfPercent != null || outdoorTemp != null || rangeKm != null
  )

  return (
    <div className="card dashboard-card vehicle-state-card">
      <div className="card-header">
        <h3 className="card-title">Vehicle State</h3>
        <span className="badge vehicle-state-badge">Last drive</span>
      </div>
      {hasReading && vehicleState ? (
        <div className="vehicle-state">
          {(rangeKm != null || outdoorTemp != null) && (
            <div className="vehicle-state-hero">
              {rangeKm != null && (
                <div className="vehicle-state-range">
                  <span className="vehicle-state-kicker">Est. range</span>
                  <div className="vehicle-state-range-row">
                    <span className="vehicle-state-range-value">{Math.round(rangeKm)}</span>
                    <span className="vehicle-state-range-unit">km</span>
                  </div>
                </div>
              )}
              {outdoorTemp != null && (
                <div className={`vehicle-state-temp${rangeKm == null ? ' vehicle-state-temp--solo' : ''}`}>
                  <span className="vehicle-state-kicker">Outside</span>
                  <span className="vehicle-state-temp-value">
                    {outdoorTemp.toFixed(PRECISION.TEMPERATURE)}°
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="vehicle-state-gauges">
            {(fuelPercent != null || fuelLiters != null) && (
              <Gauge
                label="Fuel"
                kind="fuel"
                percent={fuelPercent}
                liters={fuelLiters}
              />
            )}
            {(adbluePercent != null || adblueLiters != null) && (
              <Gauge
                label="AdBlue"
                kind="adblue"
                percent={adbluePercent}
                liters={adblueLiters}
              />
            )}
            {dpfPercent != null && (
              <Gauge
                label="DPF soot"
                kind="dpf"
                percent={dpfPercent}
              />
            )}
          </div>

          {vehicleState.last_updated_at && (
            <div className="vehicle-state-footer">
              <span className="vehicle-state-kicker">Recorded</span>
              <span className="vehicle-state-recorded">{recordedLabel(vehicleState.last_updated_at)}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="no-data">
          <p className="text-muted">No vehicle state data available</p>
        </div>
      )}
    </div>
  )
}

function Gauge({ label, kind, percent, liters }: {
  label: string
  kind: GaugeKind
  percent?: number
  liters?: number
}) {
  const tone = percent != null ? gaugeTone(kind, percent) : 'ok'
  const valueClass = tone === 'danger' ? 'text-danger' : tone === 'warn' ? 'text-warning' : ''
  const primary = percent != null ? `${formatPercent(percent)}%` : liters != null ? `${formatLiters(liters)} L` : ''
  const secondary = percent != null && liters != null ? `${formatLiters(liters)} L` : undefined

  return (
    <div className="vehicle-gauge">
      <div className="vehicle-gauge-top">
        <span className="vehicle-state-kicker">{label}</span>
        <span className="vehicle-gauge-readout">
          <span className={`vehicle-gauge-primary ${valueClass}`}>{primary}</span>
          {secondary && <span className="vehicle-gauge-secondary">{secondary}</span>}
        </span>
      </div>
      {percent != null && (
        <div
          className="progress vehicle-gauge-track"
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(clampPercent(percent))}
        >
          <div
            className={`progress-bar ${gaugeBarClass(kind, tone)}`}
            style={{ width: `${clampPercent(percent)}%` }}
          />
        </div>
      )}
    </div>
  )
}

function gaugeTone(kind: GaugeKind, percent: number): 'ok' | 'warn' | 'danger' {
  if (kind === 'dpf') {
    if (percent >= THRESHOLDS.DPF_SOOT_DANGER) return 'danger'
    if (percent >= THRESHOLDS.DPF_SOOT_WARNING) return 'warn'
    return 'ok'
  }
  const danger = kind === 'fuel' ? THRESHOLDS.FUEL_LEVEL_DANGER : THRESHOLDS.ADBLUE_LEVEL_DANGER
  const warning = kind === 'fuel' ? THRESHOLDS.FUEL_LEVEL_WARNING : THRESHOLDS.ADBLUE_LEVEL_WARNING
  if (percent <= danger) return 'danger'
  if (percent <= warning) return 'warn'
  return 'ok'
}

function gaugeBarClass(kind: GaugeKind, tone: 'ok' | 'warn' | 'danger'): string {
  if (tone === 'danger') return 'vehicle-gauge-bar--danger'
  if (tone === 'warn') return 'vehicle-gauge-bar--warn'
  if (kind === 'fuel') return 'vehicle-gauge-bar--fuel'
  if (kind === 'adblue') return 'vehicle-gauge-bar--adblue'
  return 'vehicle-gauge-bar--ok'
}

function clampPercent(percent: number): number {
  return Math.min(100, Math.max(0, percent))
}

function formatPercent(percent: number): string {
  return percent.toFixed(1)
}

function formatLiters(liters: number): string {
  return liters.toFixed(1)
}

function recordedLabel(timestamp: string): string {
  const relative = formatRelativeTime(timestamp)
  const absolute = formatTimestamp(timestamp)
  if (relative === 'Just now' || relative.endsWith('ago')) {
    return `${relative} · ${absolute}`
  }
  return absolute
}
